import { Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GetHistoryDoc } from "../FirebaseQueries";
import { Stack, Card, Typography, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Accordion, AccordionDetails, AccordionSummary, List, ListItem, CircularProgress, Divider, Paper, TextField, InputAdornment } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PieChart } from '@mui/x-charts/PieChart';
import SearchIcon from '@mui/icons-material/Search';
import { GetScenario, Scenario } from "../Scenarios/Scenarios";
import { TaskDefinition } from "../Types";
import TaskAccordion from "../components/TaskAccordion";

interface IHistoryData {
	scenarioIndex: number;
	ending: string;
	hostId: string;
	dateTimeCreated: Timestamp;
}

interface IStudent {
	id: string,
	name: string,
	role: number,
	isReady: boolean,
	sceneIndex: number,
	isHost: boolean
}

interface ITasksDict<ITask> {
	[key: number]: ITask
}

interface Task {
	id: string;
	taskIndex: number; // important
	isSolo: boolean;
	answers: IAnswers<IAnswerGroup>;
	answerFinal: IAnswerGroup[];
	chat: ITaskChatItem[];
}

interface ITaskChatItem {
	message: string,
	playerId: string,
	timeStamp: Timestamp
}

interface IAnswers<IAnswerGroup> {
	[key: string]: IAnswerGroup[];
}

interface IAnswerGroup {
	title: string;
	indexes: number[];
}

interface IChartData {
	id: number;
	value: number;
	label: string;
}

export default function HistoryGroup() {
	let params = useParams();
	const [historyData, setHistoryData] = useState<IHistoryData>({} as IHistoryData);
	const [students, setStudents] = useState<IStudent[]>([]);
	const [tasks, setTasks] = useState<Task[]>([]);
	const [fullChat, setFullChat] = useState<ITaskChatItem[]>([]);
	const [chatSearch, setChatSearch] = useState<string>("");
	const [scenario, setScenario] = useState<Scenario>({
		index: 0,
		sceneCount: 0,
		tasks: []
	});
	const [scenarioIndex, setScenarioIndex] = useState(0);

	useEffect(() => {
		const getHistoryGroup = async () => {
			try {
				const id = params.historyId !== undefined ? params.historyId : "";
				const snapshot = await GetHistoryDoc(id);
				if (snapshot.exists()) {
					setScenarioIndex(snapshot.data().scenarioIndex);
					setScenario(GetScenario(snapshot.data().scenarioIndex))
					UpdateHistoryData(snapshot.data());
					UpdatePlayers(snapshot.data().players, snapshot.data().hostId);
					UpdateTasks(snapshot.data().tasks);
					UpdateFullChat(snapshot.data().tasks);
				} else {
					// docSnap.data() will be undefined in this case
				}
			} catch (err) {
				// TODO
				console.log(err);
			}
		}

		getHistoryGroup();
	}, []);

	// function UpdateTaskDefinitions(index: number) {
	// 	if (index < Scenarios.length) {
	// 		setTaskDefinitions(Scenarios[index]);
	// 	}
	// }

	function UpdateHistoryData(data: any) {
		let newData = {} as IHistoryData;
		newData.scenarioIndex = data.scenarioIndex;
		newData.ending = data.ending;
		newData.hostId = data.hostID;
		newData.dateTimeCreated = data.dateTimeCreated;

		setHistoryData(newData);
	}

	function UpdatePlayers(players: any, hostId: string) {
		if (players === null) return;
		let newPlayers: IStudent[] = [];
		const keys: string[] = Object.keys(players);

		keys.forEach((key: string) => {
			newPlayers.push({
				id: key,
				name: players[key].name,
				role: players[key].role,
				isReady: players[key].isReady,
				sceneIndex: players[key].sceneIndex,
				isHost: key === hostId
			})
		})

		setStudents(newPlayers);
	}

	function UpdateTasks(allTasks: any) {
		const keys: string[] = Object.keys(allTasks);
		let newTasks: Task[] = [];
		// const answersCount: number = Object.keys(taskData.answers).length;

		keys.forEach((key: string) => {
			newTasks.push({
				id: allTasks[key].id,
				taskIndex: allTasks[key].taskIndex,
				isSolo: allTasks[key].isSolo,
				answers: allTasks[key].answers,
				answerFinal: allTasks[key].answerFinal,
				chat: allTasks[key].chat
			})
		})

		newTasks.sort((a, b) => a.taskIndex - b.taskIndex)
		setTasks(newTasks);
	}

	function UpdateFullChat(allTasks: any) {
		const keys: string[] = Object.keys(allTasks);
		let newFullChat: ITaskChatItem[] = []

		keys.forEach((key: string) => {
			allTasks[key].chat.forEach((element: ITaskChatItem) => {
				newFullChat.push({
					message: element.message,
					playerId: element.playerId,
					timeStamp: element.timeStamp
				})
			});
		});


		setFullChat(newFullChat);
	}

	function GetPlayerNameFromId(id: string) {
		for (let i = 0; i < students.length; i++) {
			if (students[i].id === id) return students[i].name;
		}

		return "player";
	}

	function GetAnswersCorrectScore(playerId: string, role: number): string {
		let correctCount: number = 0;
		let tasksCount: number = 0;

		scenario.tasks.forEach((taskDef) => {
			if (taskDef.roleIndexes?.includes(role)) {
				let isCorrect: boolean = true;
				tasksCount++;
				for (let i = 0; i < taskDef.correctAnswersGroups.length; i++) {
					if (!NumArraysEqual(tasks[taskDef.taskIndex].answers[playerId][i].indexes, taskDef.correctAnswersGroups[i].indexes)) {
						isCorrect = false;
						break;
					}
				}
				if (isCorrect) correctCount++;
			}
		})

		return `${correctCount} / ${tasksCount}`
	}

	function GetUserCountInRoles(roleIndexes: number[]): number {
		var count: number = 0;

		students.forEach((item: IStudent) => {
			if (roleIndexes.includes(item.role)) {
				count++;
			}
		})

		return count;
	}

	function AnswersGroupsToText(answers: IAnswerGroup[]): string {
		var value: string = "";

		answers.forEach((answerGroup: IAnswerGroup) => {
			if (answers.length > 1) value += `${answerGroup.title}: `;
			value += `${IndexesToLetters(answerGroup.indexes)} `;
		})

		return value;
	}

	function TimestampToDateHourMin(ts: Timestamp) {
		var date: Date = ts.toDate();
		var postfix: string = date.getHours() >= 12 ? "pm" : "am";
		return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")} ${postfix}`
	}

	function HandleChatSearch(event: any): void {
		setChatSearch(event.target.value);
	}

	function FullChatFiltered(): ITaskChatItem[] {
		if (chatSearch.length === 0) return fullChat;

		let chats: ITaskChatItem[] = [];

		fullChat.forEach((item) => {
			let name: string = GetPlayerNameFromId(item.playerId);
			if (item.message.toLowerCase().includes(chatSearch.toLowerCase()) || name.toLowerCase().includes(chatSearch.toLowerCase())) {
				chats.push(item);
			}
		})

		return chats;
	}

	// ---------- UI Fragments ----------

	function PlayerRoleCell(id: string, name: string, role: number, checkRole: number) {
		if (role === checkRole) {
			return (
				<Card>
					<Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
						<a href={`/app/students/${id}`}>{name}</a>
						<Typography>{GetAnswersCorrectScore(id, role)}</Typography>
					</Stack>
				</Card>
			)
		}
		else {
			return <></>;
		}
	}

	function AnswersGroupsDisplay(answerGroups: any[]) {
		return (
			<Stack sx={{ flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
				{
					answerGroups.map((group, i) => {
						return <Typography textAlign={"left"} variant="h6" key={i}>
							{answerGroups.length > 1 ? `${group.title}: ` : ""}
							{group.indexes.map((index: number) => {
								return String.fromCharCode(index + 65).toUpperCase();
							})}</Typography>
					})
				}
			</Stack>)
	}

	function TaskAnswerCharts(answers: IAnswers<IAnswerGroup>) {
		var voteMapArray: Map<string, number>[] = [];
		var chartDataArray: IChartData[][] = [];
		const keys: string[] = Object.keys(answers);

		if (answers[keys[0]] === undefined) {
			return <></>;
		}

		for (var i = 0; i < answers[keys[0]].length; i++) {
			if (i >= voteMapArray.length) {
				voteMapArray.push(new Map());
			}
		}

		keys.forEach((key: string) => {
			for (var i = 0; i < answers[key].length; i++) {
				let letters: string = IndexesToLetters(answers[key][i].indexes);
				if (voteMapArray[i].has(letters)) {
					let votes = voteMapArray[i].get(letters);
					let count = votes !== undefined ? votes + 1 : 1;
					voteMapArray[i].set(letters, count);
				} else {
					voteMapArray[i].set(letters, 1);
				}
			}
		});

		voteMapArray.forEach((voteMap, index) => {
			chartDataArray.push([]);
			let counter: number = 0;
			voteMap.forEach((value, key) => {
				let percent = (value / keys.length * 100)
				chartDataArray[index].push({ id: counter, value: (value / keys.length * 100), label: `${key} (${percent.toString()}%)` });
				counter++;
			})
		})

		return (
			<>
				{chartDataArray.map((chartData: IChartData[], index) => {
					return <PieChart
						key={index}
						series={[{ data: chartData }]}
						width={100}
						height={100}
					/>;
				})}
			</>
		)
	}

	function TaskAnswerDisplay(index: number, answerCount: number) {
		const taskData: Task = tasks[index];
		if (taskData === undefined || taskData === null) {
			return (
				<Box sx={{ minHeight: 155, display: "flex", alignItems: 'center', justifyContent: 'center' }}>
					Task Not Reached
				</Box>
			);
		} else {
			let answeredCorrectlyCount: number = GetAnsweredCorrectlyCount(index, scenario.tasks[index].correctAnswersGroups);
			let answersKeys: string[] = Object.keys(tasks[index].answers);
			let answeredCount: number = answersKeys.length;
			let percentCorrect: number = Math.round(answeredCorrectlyCount / answeredCount * 100);
			return (
				<Stack spacing={2} sx={{ mt: 2 }}>
					<Stack direction='row' sx={{ height: "200px", alignItems: 'center' }}>
						<Stack sx={{ height: "100%", alignItems: 'center', flexGrow: 1 }}>
							<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
								<Typography variant="caption">Correct Answer(s)</Typography>
							</Box>
							{AnswersGroupsDisplay(scenario.tasks[index].correctAnswersGroups)}
						</Stack>
						<Divider orientation="vertical" flexItem></Divider>
						<Stack sx={{ height: "100%", alignItems: 'center', flexGrow: 1 }}>
							<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
								<Typography variant="caption" textAlign={"left"}>Voting</Typography>
							</Box>
							<Stack spacing={1} sx={{ height: "100%" }}>
								<Stack direction={"row"} sx={{ flexGrow: 1 }}>
									{TaskAnswerCharts(taskData.answers)}
								</Stack>
								<Typography sx={{ color: 'gray' }}>{answeredCorrectlyCount}/{answeredCount} ({percentCorrect}%) answered correctly</Typography>
							</Stack>
						</Stack>
						{taskData.isSolo ? <></> :
							<>
								<Stack sx={{ height: "100%", alignItems: 'center', flexGrow: 1 }}>
									<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
										<Typography variant="caption">Final Answer</Typography>
									</Box>
									{AnswersGroupsDisplay(taskData.answerFinal)}
								</Stack>
								<Divider orientation="vertical" flexItem></Divider>
								<Stack spacing={1} sx={{ height: "100%", alignItems: 'center', flexGrow: 1, px: 1 }}>
									<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
										<Typography variant="caption">Chat</Typography>
									</Box>
									<Card variant="outlined" sx={{ width: "100%", height: "100%", boxSizing: "border-box", p: 1, overflowY: "scroll" }}>
										{taskData.chat.map((value, index) => {
											return (
												<Paper key={index} variant="outlined" sx={{ p: 1, mb: 1 }}>
													<Typography variant="subtitle2" textAlign={"left"}>{GetPlayerNameFromId(value.playerId)}</Typography>
													<Typography variant="body2" textAlign={"left"} sx={{ my: 1 }}>{value.message}</Typography>
													<Typography textAlign={"right"} variant="body2" sx={{ width: "100%", fontSize: "0.75rem", color: "gray" }}>{TimestampToDateHourMin(value.timeStamp)}</Typography>
												</Paper>
											)
										})}
									</Card>
								</Stack>
							</>
						}
					</Stack>
					<Accordion>
						<AccordionSummary
							expandIcon={<ArrowDropDownIcon />}
						>
							<Typography component="span">Submitted Answers</Typography>
						</AccordionSummary>
						<AccordionDetails>
							{answersKeys.map((answerKey, index) => {
								return <Typography key={index} textAlign={"left"}>{GetPlayerNameFromId(answerKey)} - {AnswersGroupsToText(taskData.answers[answerKey])}</Typography>
							})}
						</AccordionDetails>
					</Accordion>
				</Stack>
			);
		}
	}

	// ---------- Values ----------

	function DateString(seconds: Date): string {
		if (seconds === undefined) return "";

		let date = new Date(seconds)

		const monthNames = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December"
		];

		return `${date.getDate()} ${monthNames[date.getMonth()]}, ${date.getFullYear()}`;
	}

	function CorrectAnswersContainsIndex(correctAnswers: IAnswerGroup[], index: number, answer: string): boolean {
		for (let i = 0; i < correctAnswers.length; i++) {
			if (correctAnswers[i].indexes.includes(index)) {
				return true;
			}
		}

		return false;
	}

	function GetAnsweredCorrectlyCount(index: number, correctAnswers: IAnswerGroup[]): number {
		const answers: IAnswers<IAnswerGroup> = tasks[index].answers;
		const keys: string[] = Object.keys(answers);
		let correctCount: number = 0
		let isCorrect: boolean = true

		for (let k = 0; k < keys.length; k++) {
			isCorrect = true;
			for (let i = 0; i < answers[keys[k]].length; i++) {
				// console.log(answers[keys[k]][i]);
				// console.log(correctAnswers[i]);
				if (!NumArraysEqual(answers[keys[k]][i].indexes, correctAnswers[i].indexes)) {
					isCorrect = false;
					break;
				}
			}
			if (isCorrect) correctCount++;
		}

		return correctCount;
	}

	function NumArraysEqual(a: number[], b: number[]): boolean {
		if (a.length !== b.length) return false;

		for (let i = 0; i < a.length; i++) {
			if (!b.includes(a[i])) return false;
		}

		return true;
	}

	function GetChartDataFromTask(taskAnswers: IAnswers<IAnswerGroup>, answerCount: number): IChartData[] {
		const keys = Object.keys(taskAnswers);
		let voteMap: Map<string, number> = new Map();
		let data: IChartData[] = [];

		keys.forEach((key: string) => {
			taskAnswers[key].forEach((answerGroup: IAnswerGroup) => {
				let letters: string = IndexesToLetters(answerGroup.indexes);
				if (voteMap.has(letters)) {
					let votes = voteMap.get(letters);
					let count = votes !== undefined ? votes + 1 : 1;
					voteMap.set(letters, count);
				} else {
					voteMap.set(letters, 1);
				}
			})
		})

		let counter: number = 0;
		voteMap.forEach((value, key) => {
			data.push({ id: counter, value: value, label: key });
			counter++;
		})
		return data;
	}

	function IndexesToLetters(indexes: number[]): string {
		const sorted: number[] = indexes.sort();
		let value: string = "";

		sorted.forEach((i) => {
			if (i >= 0) {
				value += String.fromCharCode(i + 65)
			}
			else {
				value += "_";
			}
		});

		return value;
	}

	function AreAnswersMajorityWrong(index: number, answersCorrect: IAnswerGroup[]): boolean {
		if (tasks[index] === undefined) return false;
		const answers: IAnswers<IAnswerGroup> = tasks[index].answers;
		let correctCount: number = 0
		let isCorrect: boolean = true
		const keys: string[] = Object.keys(answers);
		if (keys.length === 0) return false;

		for (let k = 0; k < keys.length; k++) {
			isCorrect = true;
			for (let i = 0; i < answers[keys[k]].length; i++) {
				if (!NumArraysEqual(answers[keys[k]][i].indexes, answersCorrect[i].indexes)) {
					isCorrect = false;
					break;
				}
			}
			if (isCorrect) correctCount++;
		}

		return correctCount > keys.length / 2 ? false : true
	}

	return (
		<>
			<Stack spacing={2}>
				{/* ---------- Group ---------- */}
				<Card>
					<Stack spacing={2}>
						<Stack direction="row" sx={{ justifyContent: 'space-between' }}>
							<Typography variant="subtitle1" sx={{ textAlign: 'left' }}>Group</Typography>
							<Typography variant="subtitle1">{DateString(historyData.dateTimeCreated?.toDate())}</Typography>
						</Stack>
						<Typography sx={{ textAlign: 'left' }}>Scenario: {scenarioIndex + 1}</Typography>
						<Typography sx={{ textAlign: 'left' }}>Ending: {historyData.ending}</Typography>
						<Box>
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Nurse</TableCell>
											<TableCell>Physiotherapist</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{students.map((student: IStudent) => (
											<TableRow key={student.id}>
												<TableCell>
													{PlayerRoleCell(student.id, student.name, student.role, 0)}
												</TableCell>
												<TableCell>
													{PlayerRoleCell(student.id, student.name, student.role, 1)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					</Stack>
				</Card>
				{/* ---------- Full Chat ---------- */}
				<Card>
					<Stack spacing={1}>
						<Typography variant="subtitle1" sx={{ textAlign: 'left', mb: 1 }}>Full Chat</Typography>

						<TextField
							onChange={HandleChatSearch}
							value={chatSearch}
							placeholder="Search"
							variant="outlined"
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								},
							}} />

						<Card variant="outlined" sx={{ width: "100%", height: "300px", boxSizing: "border-box", p: 1, overflowY: "scroll" }}>
							{FullChatFiltered().map((value, index) => {
								return (
									<Paper key={index} variant="outlined" sx={{ p: 1, mb: 1 }}>
										<Typography variant="subtitle2" textAlign={"left"}>{GetPlayerNameFromId(value.playerId)}</Typography>
										<Typography variant="body2" textAlign={"left"} sx={{ my: 1 }}>{value.message}</Typography>
										<Typography textAlign={"right"} variant="body2" sx={{ width: "100%", fontSize: "0.75rem", color: "gray" }}>{TimestampToDateHourMin(value.timeStamp)}</Typography>
									</Paper>
								)
							})}
						</Card>
					</Stack>
				</Card>
				{/* ---------- Tasks ---------- */}
				<Card>
					<Stack spacing={1}>
						<Typography variant="subtitle1" sx={{ textAlign: 'left', mb: 1 }}>Tasks</Typography>
						{scenario.tasks.map((task) => (
							<TaskAccordion
								key={task.id}
								isLive={false}
								{...tasks[task.taskIndex]}
								taskDef={task}
								students={students}
								timerMax={0}
								reached={true}
							/>
							// <Box key={task.id}>
							// 	<Accordion>
							// 		<AccordionSummary expandIcon={<ArrowDropDownIcon />}>
							// 			<Stack direction="row" sx={{ width: 1, justifyContent: "space-between", mr: 1 }}>
							// 				<Typography component="span">{task.name}</Typography>
							// 				{AreAnswersMajorityWrong(task.taskIndex, task.correctAnswersGroups) ?
							// 					<WarningAmberRoundedIcon sx={{ color: "#ef5350" }}></WarningAmberRoundedIcon>
							// 					:
							// 					<></>
							// 				}
							// 			</Stack>
							// 		</AccordionSummary>
							// 		<AccordionDetails>
							// 			<Card>
							// 				<Typography textAlign="left">
							// 					{task.question}
							// 				</Typography>
							// 				<List>
							// 					{task.answers.map((answer, index) => <ListItem key={index}>{"(" + String.fromCharCode(index + 65) + ")"} {answer}</ListItem>)}
							// 				</List>
							// 			</Card>
							// 			{TaskAnswerDisplay(task.taskIndex, task.answers.length)}
							// 		</AccordionDetails>
							// 	</Accordion>
							// </Box>
						))}
					</Stack>
				</Card>
			</Stack>
		</>
	)
}